import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Chip, 
  IconButton,
  Divider
} from '@mui/material';
import { 
  ArrowBack
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectVideos } from '../store/slices/videoSlice';
import { toast } from 'react-toastify';

const VideoDetails = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // État Redux
  const videos = useSelector(selectVideos);
  const { user } = useSelector(state => state.auth);
  
  // État local
  const [video, setVideo] = useState(location.state?.video || null);
  const [product, setProduct] = useState(location.state?.product || null);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    // Si on n'a pas la vidéo dans le state de navigation, la chercher dans les vidéos chargées
    if (!video && videoId && videos.length > 0) {
      const foundVideo = videos.find(v => v.id === parseInt(videoId));
      if (foundVideo) {
        setVideo(foundVideo);
      }
    }
  }, [videoId, video, videos]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0` : null;
  };

  const handleShare = () => {
    if (navigator.share && video) {
      navigator.share({
        title: video.title,
        text: video.description,
        url: window.location.href,
      }).catch(console.error);
    } else {
      // Fallback: copier l'URL dans le presse-papiers
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Lien copié dans le presse-papiers !');
      });
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
  };


  if (!video) {
    return (
      <Box
      className="mobile-app-container"
        sx={{
          minHeight: '100vh',
          width: '100%',
          bgcolor: '#FFF7E5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Vidéo non trouvée
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleGoBack}
          startIcon={<ArrowBack />}
        >
          Retour
        </Button>
      </Box>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(video.url || video.video_url);

  return (
    <Box
    className="mobile-app-container"
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: '#FFF7E5',
        pb: 4
      }}
    >
      {/* Header avec bouton retour */}
      <Box
        sx={{
          bgcolor: '#784e3c',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <IconButton
          onClick={handleGoBack}
          sx={{ color: 'white' }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ fontFamily: 'Achiko', fontWeight: 'bold' }}>
          Lecture vidéo
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Lecteur vidéo */}
        <Paper 
          elevation={3} 
          sx={{ 
            mb: 3, 
            borderRadius: 3, 
            overflow: 'hidden',
            bgcolor: '#000'
          }}
        >
          {embedUrl ? (
            <Box
              sx={{
                position: 'relative',
                paddingBottom: '56.25%', // Ratio 16:9
                height: 0,
                overflow: 'hidden'
              }}
            >
              <iframe
                src={embedUrl}
                title={video.title}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </Box>
          ) : (
            <Box
              sx={{
                height: { xs: 200, md: 400 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                color: '#666'
              }}
            >
              <Typography variant="h6">
                Format vidéo non supporté
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Informations de la vidéo */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          {/* Titre et actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold',
                fontFamily: 'Achiko',
                color: '#333',
                flex: 1,
                mr: 2,
                minWidth: 0,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                wordBreak: 'break-word'
              }}
            >
              {video.title}
            </Typography>
            
           
          </Box>

        

          <Divider sx={{ my: 3 }} />

          {/* Description */}
          {video.description && (
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2, 
                  color: '#784e3c',
                  fontFamily: 'Achiko'
                }}
              >
                Description
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.6,
                  color: '#555',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {video.description}
              </Typography>
            </Box>
          )}

          {/* Badges pour produits virtuels */}
          {product && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip 
                label="VIDÉO" 
                sx={{ 
                  bgcolor: '#FF5722', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
              {product.is_free ? (
                <Chip 
                  label="GRATUIT" 
                  sx={{ 
                    bgcolor: '#4CAF50', 
                    color: 'white',
                    fontWeight: 'bold'
                  }} 
                />
              ) : (
                <Chip 
                  label="PREMIUM" 
                  sx={{ 
                    bgcolor: '#FF9800', 
                    color: 'white',
                    fontWeight: 'bold'
                  }} 
                />
              )}
            </Box>
          )}

          {/* Informations supplémentaires */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #eee' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {video.created_at && (
                <>Publié le {new Date(video.created_at).toLocaleDateString('fr-FR')}</>
              )}
              {video.order_index && (
                <> • Position: {video.order_index}</>
              )}
              {product && (
                <> • Produit virtuel</>
              )}
            </Typography>
          </Box>
        </Paper>
      </Box>

    </Box>
  );
};

export default VideoDetails;